import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  Cpu,
  HardDrive,
  Server,
  Wifi,
  Package,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Pager } from '@/components/pager';
import { Select } from '@/components/select';
import { useAllAssets } from '@/lib/assets';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { CollapsibleSection } from '@/components/collapsible-section';
import { StatusPill } from '@/components/status-dot';
import { groupAssetsByCategory } from '@/lib/group-assets';
import type { AssetData } from '@/types/api';

const TYPE_LABEL: Record<string, string> = {
  hardware: 'Hardware',
  software: 'Software',
  network: 'Network',
  other: 'Other',
};

const TYPE_ICON: Record<string, ComponentType<LucideProps>> = {
  hardware: HardDrive,
  software: Server,
  network: Wifi,
  other: Package,
};

const TYPE_BUCKET: Record<string, string> = {
  hardware: 'bg-cyan-500/12 text-cyan-700 dark:text-cyan-300',
  software: 'bg-violet-500/12 text-violet-700 dark:text-violet-300',
  network: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  other: 'bg-muted text-muted-foreground',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  retired: 'Retired',
  in_storage: 'In storage',
  stolen: 'Stolen',
  missing: 'Missing',
};

function statusTone(status: string): 'success' | 'neutral' | 'warning' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'retired' || status === 'in_storage') return 'neutral';
  if (status === 'missing') return 'warning';
  return 'danger';
}

export function AssetsIndex() {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data: paginated, isLoading } = useAllAssets({
    q: q || undefined,
    type: type || undefined,
    status: status || undefined,
    page,
  });

  const assets = paginated?.data;
  const meta = paginated?.meta;
  const groups = useMemo(() => groupAssetsByCategory(assets ?? []), [assets]);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Cpu}
        title="Assets"
        subtitle="Hardware, software, and network gear across every client."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name, serial, model…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          className="max-w-[160px]"
        >
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </Select>
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="max-w-[160px]"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !assets || assets.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title="No assets"
          description="Add hardware, software, or network gear from a client's detail page."
        />
      ) : (
        <div className="space-y-4">
          <div className="space-y-3">
            {groups.map(([category, groupAssets]) => (
              <CollapsibleSection key={category} title={category} count={groupAssets.length}>
                <DataList>
                  {groupAssets.map((a) => (
                    <GlobalAssetRow key={a.id} asset={a} />
                  ))}
                </DataList>
              </CollapsibleSection>
            ))}
          </div>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function GlobalAssetRow({ asset: a }: { asset: AssetData }) {
  const expired = a.days_until_warranty_expiry != null && a.days_until_warranty_expiry < 0;
  const soon =
    a.days_until_warranty_expiry != null && a.days_until_warranty_expiry <= 30 && !expired;
  const Icon = TYPE_ICON[a.type] ?? Package;
  const bucket = TYPE_BUCKET[a.type] ?? 'bg-muted text-muted-foreground';
  const subline = [a.manufacturer, a.model, a.identifier].filter(Boolean).join(' · ');

  return (
    <DataListRow interactive={false}>
      <div className={`flex size-8 shrink-0 items-center justify-center rounded-md ${bucket}`}>
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{a.name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{TYPE_LABEL[a.type] ?? a.type}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <Link to={`/clients/${a.client_id}`} className="hover:text-foreground hover:underline">
            {a.client_name ?? `Client #${a.client_id}`}
          </Link>
          {subline ? (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{subline}</span>
            </>
          ) : null}
        </div>
      </div>
      {a.warranty_expires_at ? (
        <StatusPill tone={expired ? 'danger' : soon ? 'warning' : 'neutral'}>
          {expired ? <AlertTriangle className="size-3" /> : <CalendarClock className="size-3" />}
          {expired
            ? `Expired ${Math.abs(a.days_until_warranty_expiry!)} d ago`
            : `${a.days_until_warranty_expiry} d`}
        </StatusPill>
      ) : null}
      <StatusPill tone={statusTone(a.status)}>{STATUS_LABEL[a.status] ?? a.status}</StatusPill>
    </DataListRow>
  );
}
